import { assert, error } from '../app/logger.js';
import { parseGpx } from '../util/gpx_parser.js';
import { GpxTrack } from './gpx_track.js';

/**
 * An object representing gpx data.
 */
class GpxCollection {
  constructor({ name, map } = {}) {
    this.#name = name;
    this.#map = map;
  }

  static findPointIn(points, date) {
    let sortedPoints = [...points].sort((a, b) => a.date - b.date);
    let prevPoint;
    let curPoint;
    for (let point of sortedPoints) {
      if (point.date < date) {
        prevPoint = point;
        continue;
      }
      curPoint = point;
      break;
    }
    if (!prevPoint) {
      return curPoint;
    }
    if (!curPoint) {
      return prevPoint;
    }
    return curPoint.date - date < date - prevPoint.date ? curPoint : prevPoint;
  }

  get name() {
    return this.#name;
  }

  setData(gpxContent) {
    assert(gpxContent, `[map] no gpx content`);
    this.clear();
    this.#data = parseGpx(gpxContent);
    return this;
  }

  get metaData() {
    let m = this.#data && this.#data.metadata;
    return {
      creator: m && m.creator,
      date: m && m.time,
      bounds: m && m.bounds,
      name: m && m.name,
      description: m && m.description,
      author: m && m.author,
      copyright: m && m.copyright,
      links: m && m.links,
      keywords: m && m.keywords,
    };
  }

  get tracksData() {
    if (!this.#tracksData) {
      this.#tracksData =
        (this.#data &&
          this.#data.tracks.map(t => ({
            name: t.name,
            distance: t.distance,
            elevation: t.elevation,
            slopes: t.slopes,
            points: t.points.map(p => ({
              lat: p.lat,
              lng: p.lon,
              elevation: p.ele,
              date: p.time,
            })),
          }))) ||
        [];
    }
    return this.#tracksData;
  }

  findPoint(date) {
    let points = this.tracksData.flatMap(t => t.points);
    return this.constructor.findPointIn(points, date);
  }

  get activeTracks() {
    return this.tracks.filter(track => track.active);
  }

  get tracks() {
    if (!this.#tracks) {
      this.#tracks = this.tracksData.map(
        ({ name, points }) =>
          new GpxTrack({
            map: this.#map,
            collection: this,
            name,
            path: points,
            clickable: true,
            gradientType: 'time',
          })
      );
    }
    return this.#tracks;
  }

  showTracks() {
    for (let track of this.tracks) {
      track.show();
    }
  }

  hideTracks() {
    for (let track of this.#tracks || []) {
      track.hide();
    }
  }

  splitTrack(track, point) {
    let index = this.tracks.indexOf(track);
    if (index == -1) {
      error(`[map] no gpx track to cut is found`);
      return;
    }

    let [startPath, endPath] = track.splitPath(point);

    this.#tracksData.splice(
      index,
      1,
      {
        points: startPath,
      },
      {
        points: endPath,
      }
    );

    track.path = startPath;
    let endTrack = new GpxTrack({
      map: this.#map,
      collection: this,
      name: track.name,
      path: endPath,
      clickable: true,
      gradientType: 'time',
    });
    this.#tracks.splice(index, 1, track, endTrack);
    if (track.isShown()) {
      endTrack.show();
    }
  }

  deleteTrack(track) {
    let index = this.tracks.indexOf(track);
    if (index == -1) {
      error(`[map] no gpx track to delete is found`);
      return;
    }

    this.#tracks[index].hide();
    this.#tracksData.splice(index, 1);
    this.#tracks.splice(index, 1);
  }

  clear() {
    this.hideTracks();
    this.#tracks = null;
    this.#tracksData = null;
  }

  #data;
  #map;
  #name;
  #tracks;
  #tracksData;
}

export { GpxCollection };
